import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logoSource from '../assets/noble_syntax_home.png';

// ─────────────────────────────────────────────────────────────────────────────
// KoBoToolbox Enketo URL
// ─────────────────────────────────────────────────────────────────────────────
// const ENKETO_URL = 'https://ee.kobotoolbox.org/i/7qtoOttb';
const ENKETO_URL = 'https://ee.kobotoolbox.org/i/hcPiMGl7';

// ─────────────────────────────────────────────────────────────────────────────
// KEYFRAME & RESPONSIVE CSS INJECTION
// ─────────────────────────────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('ns-enum-styles')) return;
  const style = document.createElement('style');
  style.id = 'ns-enum-styles';
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.8); }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }

    /* ── LAYOUT ────────────────────────────────────────── */
    .ns-enum-root {
      display: flex;
      flex-direction: row;
      min-height: 100vh;
      background: #080A0F;
      font-family: 'Outfit', sans-serif;
      color: #E8EFF8;
      overflow: hidden;
    }

    /* ── LEFT PANEL (fixed sidebar) ────────────────────── */
    .ns-enum-sidebar {
      position: fixed;
      top: 0;
      left: 0;
      width: 35vw;
      height: 100vh;
      display: flex;
      flex-direction: column;
      background: linear-gradient(160deg, #0D1220 0%, #080A0F 60%, #0A0F1A 100%);
      border-right: 1px solid rgba(56, 182, 255, 0.10);
      overflow: hidden;
      z-index: 10;
    }

    /* decorative glow blob behind sidebar content */
    .ns-enum-sidebar::before {
      content: '';
      position: absolute;
      top: -80px;
      left: -60px;
      width: 340px;
      height: 340px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(56,182,255,0.09) 0%, transparent 70%);
      pointer-events: none;
    }
    .ns-enum-sidebar::after {
      content: '';
      position: absolute;
      bottom: -60px;
      right: -80px;
      width: 280px;
      height: 280px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(79,195,247,0.06) 0%, transparent 70%);
      pointer-events: none;
    }

    /* ── SIDEBAR INNER ──────────────────────────────────── */
    .ns-enum-sidebar-inner {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 32px 36px 28px;
      gap: 0;
    }

    /* ── NAV LOGO ───────────────────────────────────────── */
    .ns-enum-logo {
      height: 30px;
      object-fit: contain;
      cursor: pointer;
      margin-bottom: 40px;
      align-self: flex-start;
      opacity: 0.92;
      transition: opacity 0.2s;
    }
    .ns-enum-logo:hover { opacity: 1; }

    /* ── BADGE ──────────────────────────────────────────── */
    .ns-enum-badge {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      background: linear-gradient(135deg, rgba(56,182,255,0.14), rgba(75,195,247,0.07));
      border: 1px solid rgba(56,182,255,0.28);
      border-radius: 100px;
      padding: 5px 14px;
      padding-left: 0px;
      margin-left: 0px;
      font-size: 10.5px;
      color: #4FC3F7;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 22px;
      width: fit-content;
      animation: fadeInUp 0.5s ease both;
    }
    .ns-enum-badge-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #4FC3F7;
      box-shadow: 0 0 8px rgba(79,195,247,0.9);
      animation: pulse 2s ease-in-out infinite;
    }

    /* ── ROLE LABEL ─────────────────────────────────────── */
    .ns-enum-role-label {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: rgba(56,182,255,0.09);
      border: 1px solid rgba(56,182,255,0.18);
      border-radius: 4px;
      padding: 4px 10px;
      font-size: 10px;
      color: #38B6FF;
      font-weight: 700;
      letter-spacing: 0.10em;
      text-transform: uppercase;
      margin-bottom: 14px;
      width: fit-content;
      animation: fadeInUp 0.5s 0.1s ease both;
    }

    /* ── HERO TITLE ─────────────────────────────────────── */
    .ns-enum-title {
      font-size: clamp(22px, 2.4vw, 34px);
      font-weight: 800;
      color: #F0F6FF;
      line-height: 1.15;
      letter-spacing: -0.025em;
      margin: 0 0 12px;
      animation: fadeInUp 0.5s 0.15s ease both;
    }
    .ns-enum-title-accent {
      background: linear-gradient(90deg, #38B6FF, #4FC3F7, #38B6FF);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: shimmer 3s linear infinite;
    }

    /* ── HERO SUBTITLE ──────────────────────────────────── */
    .ns-enum-sub {
      font-size: 13.5px;
      color: #6A8099;
      line-height: 1.7;
      margin: 0 0 32px;
      animation: fadeInUp 0.5s 0.2s ease both;
    }

    /* ── DIVIDER ────────────────────────────────────────── */
    .ns-enum-divider {
      width: 40px;
      height: 2px;
      background: linear-gradient(90deg, #38B6FF, transparent);
      border-radius: 2px;
      margin-bottom: 24px;
      animation: fadeInUp 0.5s 0.25s ease both;
    }

    /* ── META CHIPS ─────────────────────────────────────── */
    .ns-enum-chips {
      display: flex;
      flex-direction: column;
      gap: 10px;
      flex: 1;
      animation: fadeInUp 0.5s 0.3s ease both;
    }
    .ns-enum-chip {
      display: flex;
      align-items: center;
      gap: 12px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.065);
      border-radius: 10px;
      padding: 11px 14px;
      font-size: 12.5px;
      color: #B8CEE2;
      font-weight: 500;
      transition: border-color 0.2s, background 0.2s;
    }
    .ns-enum-chip:hover {
      border-color: rgba(56,182,255,0.22);
      background: rgba(56,182,255,0.05);
    }
    .ns-enum-chip-icon {
      width: 30px;
      height: 30px;
      border-radius: 7px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      flex-shrink: 0;
    }

    /* ── SIDEBAR FOOTER ─────────────────────────────────── */
    .ns-enum-sidebar-footer {
      margin-top: auto;
      padding-top: 24px;
      border-top: 1px solid rgba(255,255,255,0.05);
      font-size: 11px;
      color: #334455;
      line-height: 1.6;
    }
    .ns-enum-sidebar-footer a {
      color: #38B6FF;
      text-decoration: none;
    }
    .ns-enum-sidebar-footer a:hover { text-decoration: underline; }

    /* ── RIGHT PANEL (scrollable form area) ─────────────── */
    .ns-enum-main {
      margin-left: 35vw;
      width: 65vw;
      height: 100vh;
      display: flex;
      flex-direction: column;
      background: #FFFFFF;
      overflow: hidden;
      /* background: #080A0F; */
    }

    /* ── FORM TOPBAR ────────────────────────────────────── */
    .ns-enum-formbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 32px;
      border-bottom: 1px solid #FFFFFF;
      background: #FFFFFF;
      backdrop-filter: blur(10px);
      position: sticky;
      top: 0;
      z-index: 5;
      flex-shrink: 0;
    }
    .ns-enum-formbar-label {
      font-size: 12px;
      color: #3E5A70;
      font-weight: 500;
    }
    .ns-enum-formbar-secure {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11.5px;
      color: #2E4A60;
    }

    /* ── IFRAME WRAPPER ─────────────────────────────────── */
    .ns-enum-iframe-wrap {
      flex: 1;
      position: relative;
      overflow: hidden;
      min-height: 0;
    }
    .ns-enum-loading {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      /*background: #080A0F;*/
      background: #FFFFFF;
      gap: 18px;
      z-index: 4;
    }
    .ns-enum-spinner {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      border: 2.5px solid rgba(56,182,255,0.14);
      border-top-color: #38B6FF;
      animation: spin 0.85s linear infinite;
    }
    .ns-enum-loading-text {
      font-size: 13px;
      color: #3A7090;
      font-weight: 500;
      letter-spacing: 0.04em;
    }
    .ns-enum-iframe {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border: none;
      display: block;
      transition: opacity 0.4s ease;
    }

    /* ════════════════════════════════════════════════════
       TABLET  (768px – 1099px)
       Same split layout but sidebar shrinks slightly
    ════════════════════════════════════════════════════ */
    @media (max-width: 1099px) and (min-width: 768px) {
      .ns-enum-sidebar { width: 38vw; }
      .ns-enum-main    { margin-left: 38vw; width: 62vw; height: 100vh; overflow: hidden; }
      .ns-enum-sidebar-inner { padding: 24px 26px 20px; }
      .ns-enum-title   { font-size: clamp(18px, 3vw, 26px); }
      .ns-enum-chips   { gap: 8px; }
      .ns-enum-chip    { font-size: 12px; padding: 9px 12px; }
    }

    /* ════════════════════════════════════════════════════
       MOBILE  (< 768px)
       Stack: compact header on top, full-screen form below
    ════════════════════════════════════════════════════ */
    @media (max-width: 767px) {
      .ns-enum-root {
        flex-direction: column;
        overflow: auto;
      }

      /* Sidebar becomes a compact top header */
      .ns-enum-sidebar {
        position: relative;
        width: 100%;
        height: auto;
        border-right: none;
        border-bottom: 1px solid rgba(56,182,255,0.10);
        overflow: visible;
      }
      .ns-enum-sidebar::before,
      .ns-enum-sidebar::after { display: none; }

      .ns-enum-sidebar-inner {
        padding: 20px 20px 16px;
        gap: 0;
        flex-direction: column;
      }

      /* Logo + badge on one row */
      .ns-enum-mobile-toprow {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 18px;
      }
      .ns-enum-logo { margin-bottom: 0; height: 26px; }

      /* Title compact */
      .ns-enum-title { font-size: 22px; margin-bottom: 8px; }
      .ns-enum-sub   { font-size: 13px; margin-bottom: 16px; }
      .ns-enum-role-label { margin-bottom: 10px; }
      .ns-enum-divider { display: none; }

      /* Chips horizontal scroll row */
      .ns-enum-chips {
        flex-direction: row;
        overflow-x: auto;
        gap: 8px;
        padding-bottom: 4px;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
      }
      .ns-enum-chips::-webkit-scrollbar { display: none; }
      .ns-enum-chip {
        flex-shrink: 0;
        white-space: nowrap;
        font-size: 12px;
        padding: 8px 12px;
      }
      .ns-enum-chip-icon { width: 24px; height: 24px; font-size: 12px; }

      /* Sidebar footer hidden on mobile — shown in main footer */
      .ns-enum-sidebar-footer { display: none; }

      /* Main panel full width */
      .ns-enum-main {
        margin-left: 0;
        width: 100%;
        height: auto;
        overflow: visible;
      }

      .ns-enum-iframe-wrap {
        min-height: 100svh;
        position: relative;
      }

      /* On mobile the iframe can't be absolute inside auto-height parent */
      .ns-enum-iframe {
        position: static;
        width: 100%;
        height: 100svh;
        display: block;
      }
    }
  `;
  document.head.appendChild(style);
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const EnumeratorApply = () => {
  const navigate = useNavigate();
  const [formLoaded, setFormLoaded] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    document.title = 'Enumerator Application — Noble Syntax Limited';
    if (!document.querySelector('link[href*="Outfit"]')) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    injectStyles();
  }, []);

  return (
    <div className="ns-enum-root">

      {/* ══════════════════════════════════════════════════
          LEFT SIDEBAR — fixed, 35 vw
      ══════════════════════════════════════════════════ */}
      <aside className="ns-enum-sidebar">
        <div className="ns-enum-sidebar-inner">

          {/* Mobile: logo + badge in one row */}
          <div className="ns-enum-mobile-toprow">
            <img
              src={logoSource}
              alt="Noble Syntax"
              className="ns-enum-logo"
              onClick={() => navigate('/')}
            />
            {/* Badge — visible on mobile in top row, desktop via normal flow */}
            <div className="ns-enum-badge" style={{ marginBottom: 0 }}>
              <div className="ns-enum-badge-dot" />
              Now Accepting
            </div>
          </div>

          {/* Badge for desktop (hidden on mobile via the row above) */}
          <div className="ns-enum-badge ns-enum-badge--desktop" style={{ display: 'none' }}>
            <div className="ns-enum-badge-dot" />
            Now Accepting Applications
          </div>

          {/* Role label */}
          <div className="ns-enum-role-label">
            <span>📋</span> Open Position
          </div>

          {/* Title */}
          <h1 className="ns-enum-title">
            Field Enumerator<br />
            <span className="ns-enum-title-accent">Recruitment Drive</span>
          </h1>

          {/* Subtitle */}
          <p className="ns-enum-sub">
            Noble Syntax Limited is recruiting experienced field enumerators
            for upcoming research projects. Complete the form to apply —
            all responses go directly and securely to our team.
          </p>

          <div className="ns-enum-divider" />

          {/* Meta chips */}
          <div className="ns-enum-chips">
            <Chip emoji="📍" label="Zambia"               bg="rgba(56,182,255,0.10)" />
            <Chip emoji="📅" label="Short-term contract"  bg="rgba(124,58,237,0.13)" />
            <Chip emoji="💼" label="Field enumerator"     bg="rgba(5,150,105,0.13)"  />
            <Chip emoji="🎓" label="Training provided"    bg="rgba(245,158,11,0.11)" />
          </div>

          {/* Sidebar footer */}
          <div className="ns-enum-sidebar-footer">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: 5 }}>
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
            Responses submitted securely via KoBoToolbox.{' '}
            Questions?{' '}
            <a href="mailto:info@noblesyntax.com">info@noblesyntax.com</a>
          </div>
        </div>
      </aside>

      {/* ══════════════════════════════════════════════════
          RIGHT MAIN — scrollable, 65 vw
      ══════════════════════════════════════════════════ */}
      <main className="ns-enum-main">

        {/* Sticky top bar */}
        <div className="ns-enum-formbar">
          <span className="ns-enum-formbar-label">
            Application Form — Noble Syntax Enumerator Recruitment
          </span>
          <span className="ns-enum-formbar-secure">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Secure · KoBoToolbox
          </span>
        </div>

        {/* iFrame */}
        <div className="ns-enum-iframe-wrap">
          {!formLoaded && (
            <div className="ns-enum-loading">
              <div className="ns-enum-spinner" />
              <span className="ns-enum-loading-text">Loading Application Form…</span>
            </div>
          )}
          <iframe
            src={ENKETO_URL}
            className="ns-enum-iframe"
            style={{ opacity: formLoaded ? 1 : 0 }}
            width="100%" height="100%"
            title="Noble Syntax Enumerator Recruitment Form"
            allow="geolocation *; camera *; microphone *"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
            scrolling="yes"
            onLoad={() => setFormLoaded(true)}
          />
        </div>

        {/* Mobile-only footer note */}
        <div style={{
          display: 'none',
          padding: '12px 20px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          fontSize: '11px',
          color: '#334455',
          textAlign: 'center',
          background: 'rgba(0,0,0,0.3)',
        }}
          className="ns-enum-mobile-footer"
        >
          Responses submitted securely via KoBoToolbox.{' '}
          <a href="mailto:info@noblesyntax.com" style={{ color: '#38B6FF', textDecoration: 'none' }}>
            info@noblesyntax.com
          </a>
        </div>
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CHIP SUB-COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const Chip = ({ emoji, label, bg }) => (
  <div className="ns-enum-chip">
    <div className="ns-enum-chip-icon" style={{ background: bg }}>
      {emoji}
    </div>
    <span>{label}</span>
  </div>
);

export default EnumeratorApply;